import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@paguz/ui';
import {
  QrCodeIcon,
  ReceiptTextIcon,
  SparklesIcon,
  WalletIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

/** Tour de primera vez (4 pasos). Se recuerda por dispositivo; se puede repetir desde la guía. */
export const TOUR_KEY = 'paguz.web.tour.v1';

const STEPS: readonly { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: WalletIcon,
    title: 'Tu cupo, siempre a la vista',
    body: 'En Inicio ves cuánto cupo tienes disponible, tu próximo pago y la fecha. Toca el ojo para ocultar los montos.',
  },
  {
    icon: QrCodeIcon,
    title: 'Paga con QR en cuotas',
    body: 'Escanea el QR Bre-B del comercio, elige en cuántas cuotas y confirma con tu código. El comercio recibe al instante.',
  },
  {
    icon: SparklesIcon,
    title: 'Tu Asesor IA',
    body: 'Escríbele o háblale: te ayuda a pedir tu crédito, a pagarle a un proveedor y a revisar tus cuotas. Los pagos siempre los confirmas tú.',
  },
  {
    icon: ReceiptTextIcon,
    title: 'Movimientos y extracto',
    body: 'Revisa cada compra con su plan de cuotas, tus devoluciones y el extracto del mes. Si algo salió mal, abre un reclamo desde la compra.',
  },
];

export function tourSeen(): boolean {
  try {
    return globalThis.localStorage.getItem(TOUR_KEY) === 'done';
  } catch {
    return true;
  }
}

export function resetTour(): void {
  try {
    globalThis.localStorage.removeItem(TOUR_KEY);
  } catch {
    // sin almacenamiento
  }
}

export function FirstRunTour() {
  const [open, setOpen] = useState(() => !tourSeen());
  const [step, setStep] = useState(0);
  const close = () => {
    try {
      globalThis.localStorage.setItem(TOUR_KEY, 'done');
    } catch {
      // sin almacenamiento: se mostrará de nuevo
    }
    setOpen(false);
  };
  const current = STEPS[step] ?? STEPS[0];
  if (!current) return null;
  const Icon = current.icon;
  const last = step === STEPS.length - 1;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="max-w-[360px]" aria-describedby="tour-body">
        <DialogHeader className="items-center text-center">
          <span className="mb-2 flex size-16 items-center justify-center rounded-2xl bg-highlight-soft text-highlight animate-in zoom-in-75">
            <Icon className="size-8" aria-hidden />
          </span>
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription id="tour-body">{current.body}</DialogDescription>
        </DialogHeader>
        <div
          className="flex justify-center gap-1.5"
          aria-label={`Paso ${String(step + 1)} de ${String(STEPS.length)}`}
        >
          {STEPS.map((s, index) => (
            <span
              key={s.title}
              aria-hidden
              className={cn(
                'h-2 rounded-full transition-all',
                index === step ? 'w-6 bg-highlight' : 'w-2 bg-muted-foreground/30',
              )}
            />
          ))}
        </div>
        <DialogFooter className="flex-row gap-2">
          <Button variant="ghost" className="flex-1" onClick={close}>
            Saltar
          </Button>
          <Button
            variant="highlight"
            className="flex-1"
            onClick={() => {
              if (last) close();
              else setStep((n) => n + 1);
            }}
          >
            {last ? '¡Empecemos!' : 'Siguiente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
